import { JoinInvitation, MembershipPage, SessionsPanel } from "./members";
import { IdentityLinkScreen, readIdentityLink } from "./identity";
import { createNativeHistory } from "./native-history";
import { GoogleSignIn, readGoogleAuthError } from "./google-sign-in";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  Bell,
  ArrowDownToLine,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  CalendarDays,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleCheck,
  Clock3,
  File,
  FileSpreadsheet,
  FileText,
  Folder,
  FolderOpen,
  FolderKanban,
  Grid2X2,
  Home,
  Info,
  LayoutGrid,
  ListTodo,
  LoaderCircle,
  LogOut,
  Menu,
  MessageSquare,
  MoreHorizontal,
  Plus,
  Presentation,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  Table2,
  Trash2,
  Upload,
  Users,
  Video,
  X,
  Mail,
  BookOpen,
  Workflow,
  RotateCcw,
  Pencil,
  CheckSquare2,
  PanelLeftClose,
  type LucideIcon,
} from "lucide-react";
import type {
  Invitation,
  Page,
  Resource,
  SessionResponse,
  WorkspaceMember,
  WorkspaceSummary,
} from "@sol-office/contracts";
import {
  api,
  downloadFile,
  errorMessage,
  patch,
  post,
  RequestError,
  uploadFile,
} from "./api";
import {
  EmptyState,
  ErrorNotice,
  Field,
  formatBytes,
  formatDate,
  initials,
  Modal,
  PageHeading,
  SolMark,
  Spinner,
  TextLink,
} from "./ui";

import { HomePage } from "./home";
import { NotificationWidget, NotificationsPage } from "./notifications";
const TasksPage = lazy(() =>
  import("./tasks").then((m) => ({ default: m.TasksPage })),
);
const ProjectsPage = lazy(() =>
  import("./projects").then((module) => ({ default: module.ProjectsPage })),
);
const TaskCreateDialog = lazy(() =>
  import("./tasks").then((m) => ({ default: m.TaskCreateDialog })),
);

type View =
  "home" | "files" | "tasks" | "members" | "settings" | "apps" | string;
const DocumentsPage = lazy(() =>
  import("./documents").then((module) => ({ default: module.DocumentsPage })),
);
const ChatPage = lazy(() =>
  import("./chat").then((module) => ({ default: module.ChatPage })),
);
const CalendarPage = lazy(() =>
  import("./calendar").then((module) => ({ default: module.CalendarPage })),
);

type Application = {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  color: string;
  available?: boolean;
};
const applications: Application[] = [
  {
    id: "notifications",
    label: "Inbox",
    description: "Your updates, reminders, and saved digests.",
    icon: Bell,
    color: "ochre",
    available: true,
  },
  {
    id: "files",
    label: "Files",
    description: "A shared home for your team’s work.",
    icon: FolderOpen,
    color: "ochre",
    available: true,
  },
  {
    id: "tasks",
    label: "Tasks",
    description: "Turn your plans into the next step.",
    icon: CheckSquare2,
    color: "green",
    available: true,
  },
  {
    id: "projects",
    label: "Projects",
    description: "Plan tasks, dependencies, recurrence and workload together.",
    icon: FolderKanban,
    color: "green",
    available: true,
  },
  {
    id: "documents",
    label: "Documents",
    description: "Write, refine and create together.",
    icon: FileText,
    color: "blue",
    available: true,
  },
  {
    id: "spreadsheets",
    label: "Spreadsheets",
    description: "Bring clarity to your numbers.",
    icon: FileSpreadsheet,
    color: "green",
  },
  {
    id: "slides",
    label: "Slides",
    description: "Give your ideas a beautiful stage.",
    icon: Presentation,
    color: "coral",
  },
  {
    id: "pdf",
    label: "PDF",
    description: "Read, edit and work with any PDF.",
    icon: File,
    color: "rose",
  },
  {
    id: "chat",
    label: "Chat",
    description: "Keep conversations close to the work.",
    icon: MessageSquare,
    color: "blue",
    available: true,
  },
  {
    id: "calendar",
    label: "Calendar",
    description: "Make space for what comes next.",
    icon: CalendarDays,
    color: "coral",
    available: true,
  },
  {
    id: "meetings",
    label: "Meetings",
    description: "Connect, discuss and move forward.",
    icon: Video,
    color: "violet",
  },
  {
    id: "wiki",
    label: "Wiki",
    description: "Make your team’s knowledge useful.",
    icon: BookOpen,
    color: "ochre",
  },
  {
    id: "base",
    label: "Base",
    description: "Organize data around the way you work.",
    icon: Table2,
    color: "violet",
  },
  {
    id: "approvals",
    label: "Approvals",
    description: "A clear path from request to decision.",
    icon: Workflow,
    color: "green",
  },
  {
    id: "mail",
    label: "Mail",
    description: "Stay connected, with all the context.",
    icon: Mail,
    color: "blue",
  },
  {
    id: "ai",
    label: "Sol AI",
    description: "Thoughtful assistance across your work.",
    icon: Sparkles,
    color: "forest",
  },
];

function useRoute() {
  const [path, setPath] = useState(
    () => `${location.pathname}${location.search}${location.hash}`,
  );
  const [historyNotice, setHistoryNotice] = useState("");
  const nativeHistory = useRef<ReturnType<typeof createNativeHistory> | null>(
    null,
  );
  useLayoutEffect(() => {
    nativeHistory.current ??= createNativeHistory({
      onChange: () =>
        setPath(`${location.pathname}${location.search}${location.hash}`),
      onRecovery: setHistoryNotice,
    });
    return nativeHistory.current.install();
  }, []);
  const navigate = useCallback((next: string) => {
    if (
      !window.dispatchEvent(
        new Event("sol:before-navigate", { cancelable: true }),
      )
    )
      return;
    history.pushState({}, "", next);
    window.dispatchEvent(new Event("sol:route-changed"));
  }, []);
  const [, prefix, workspaceId, view] = new URL(
    path,
    location.origin,
  ).pathname.split("/");
  return {
    workspaceId: prefix === "w" ? workspaceId : undefined,
    view: (view ?? "home") as View,
    navigate,
    historyNotice,
    dismissHistoryNotice: () => setHistoryNotice(""),
  };
}

export default function App() {
  const [identityLink, setIdentityLink] = useState(readIdentityLink);
  const [googleAuthError, setGoogleAuthError] = useState(readGoogleAuthError);
  const [authRequest, setAuthRequest] = useState<
    "verification" | "recovery" | undefined
  >();
  const [joinToken, setJoinToken] = useState(() =>
    window.location.pathname === "/join" &&
    /^[A-Za-z0-9_-]{43}$/.test(window.location.hash.slice(1))
      ? window.location.hash.slice(1)
      : null,
  );
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [startupError, setStartupError] = useState("");
  const route = useRoute();
  useEffect(() => {
    const url = new URL(location.href);
    if (!url.searchParams.has("authError")) return;
    url.searchParams.delete("authError");
    history.replaceState(history.state, "", url);
  }, []);
  useEffect(() => {
    if (joinToken) window.history.replaceState({}, "", "/join");
  }, [joinToken]);
  useEffect(() => {
    if (identityLink)
      window.history.replaceState({}, "", window.location.pathname);
  }, [identityLink]);
  const loadSession = useCallback(async () => {
    setStartupError("");
    setLoading(true);
    try {
      setSession(await api<SessionResponse>("/auth/me"));
    } catch (error) {
      if (!(error instanceof RequestError && error.status === 401))
        setStartupError(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void loadSession();
  }, [loadSession]);
  useEffect(() => {
    const expired = () => setSession(null);
    window.addEventListener("sol:session-expired", expired);
    return () => window.removeEventListener("sol:session-expired", expired);
  }, []);
  const workspace =
    session?.workspaces.find((item) => item.id === route.workspaceId) ??
    session?.workspaces[0];
  useEffect(() => {
    if (
      !joinToken &&
      !identityLink &&
      !authRequest &&
      workspace &&
      workspace.id !== route.workspaceId
    )
      route.navigate(`/w/${workspace.id}/home`);
  }, [
    workspace?.id,
    route.workspaceId,
    route.navigate,
    joinToken,
    identityLink,
    authRequest,
  ]);
  if (identityLink)
    return (
      <IdentityLinkScreen
        link={identityLink}
        onConsumed={() =>
          setIdentityLink((value) => (value ? { ...value, token: null } : null))
        }
        onReset={() => setSession(null)}
        onReturn={(request) => {
          setAuthRequest(request);
          setIdentityLink(null);
          route.navigate("/");
          // Refresh a possibly verified session rather than trusting its old profile.
          if (!request) void loadSession();
        }}
      />
    );
  if (loading)
    return (
      <div className="boot-screen">
        <div className="brand">
          <SolMark />
          <span>
            sol<span className="brand-office">office</span>
          </span>
        </div>
        <Spinner label="Opening your workspace…" />
      </div>
    );
  if (startupError)
    return (
      <div className="boot-screen">
        <SolMark />
        <h1>Let’s reconnect.</h1>
        <p>Your workspace couldn’t be reached.</p>
        <ErrorNotice message={startupError} />
        <button className="button primary" onClick={() => void loadSession()}>
          Try again
        </button>
      </div>
    );
  if (!session || authRequest)
    return (
      <AuthScreen
        initialRequest={authRequest}
        googleAuthError={googleAuthError}
        clearGoogleAuthError={() => setGoogleAuthError("")}
        invitationPending={Boolean(joinToken)}
        onLogin={(value) => {
          setAuthRequest(undefined);
          setGoogleAuthError("");
          setSession(value);
          if (!joinToken && value.workspaces[0])
            route.navigate(`/w/${value.workspaces[0].id}/home`);
        }}
      />
    );
  if (joinToken)
    return (
      <JoinInvitation
        token={joinToken}
        user={session.user}
        onCancel={() => setJoinToken(null)}
        onJoined={async (joined) => {
          setSession(await api<SessionResponse>("/auth/me"));
          setJoinToken(null);
          route.navigate(`/w/${joined.id}/home`);
        }}
      />
    );
  return (
    <WorkspaceApp
      session={session}
      setSession={setSession}
      workspace={workspace}
      view={route.view}
      navigate={route.navigate}
      historyNotice={route.historyNotice}
      dismissHistoryNotice={route.dismissHistoryNotice}
    />
  );
}

function AuthScreen({
  onLogin,
  initialRequest,
  googleAuthError,
  clearGoogleAuthError,
  invitationPending,
}: {
  onLogin: (session: SessionResponse) => void;
  initialRequest?: "verification" | "recovery";
  googleAuthError: string;
  clearGoogleAuthError: () => void;
  invitationPending: boolean;
}) {
  const [mode, setMode] = useState<
    "login" | "register" | "verification" | "recovery"
  >(initialRequest ?? "login");
  const [pending, setPending] = useState(false);
  const [googlePending, setGooglePending] = useState(false);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [email, setEmail] = useState("");
  const requestingLink = mode === "verification" || mode === "recovery";
  const busy = pending || googlePending;
  const changeMode = (next: typeof mode) => {
    setMode(next);
    setError("");
    clearGoogleAuthError();
    setAccepted(false);
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    const form = new FormData(event.currentTarget);
    setPending(true);
    setError("");
    clearGoogleAuthError();
    try {
      if (mode === "register") {
        await post("/auth/register", {
          name: form.get("name"),
          email,
          password: form.get("password"),
          workspaceName: form.get("workspaceName"),
        });
        setAccepted(true);
        setMode("login");
      } else if (requestingLink) {
        await post(`/auth/${mode}/request`, { email });
        setAccepted(true);
      } else
        onLogin(
          await post<SessionResponse>("/auth/login", {
            email,
            password: form.get("password"),
          }),
        );
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setPending(false);
    }
  };
  return (
    <main className="auth-layout">
      <section className="auth-story">
        <a className="brand" href="/" aria-label="Sol Office home">
          <SolMark />
          <span>
            sol<span className="brand-office">office</span>
          </span>
        </a>
        <div className="auth-story-content">
          <span className="eyebrow">A little more room to think</span>
          <h1>
            Good work <br />
            starts <em>together.</em>
          </h1>
          <p>
            A calmer place for your ideas, your team,
            <br className="desktop-only" /> and everything you’re working
            towards.
          </p>
          <div className="auth-art" aria-hidden="true">
            <div className="art-orbit orbit-one" />
            <div className="art-orbit orbit-two" />
            <div className="art-card art-back">
              <div />
              <div />
              <div />
            </div>
            <div className="art-card art-front">
              <SolMark small />
              <div className="art-line long" />
              <div className="art-line" />
              <div className="art-line short" />
              <div className="art-check">
                <Check size={19} />
              </div>
            </div>
            <span className="art-spark">
              <Sparkles size={23} />
            </span>
            <span className="art-disc" />
          </div>
        </div>
        <div className="auth-story-footer">
          <span>One workspace. Many possibilities.</span>
          <span className="story-dot" />
        </div>
      </section>
      <section className="auth-form-side">
        <div className="auth-topline">
          <span>
            {mode === "login" ? "New here?" : "Already have a workspace?"}
          </span>
          <button
            disabled={busy}
            onClick={() => {
              changeMode(mode === "login" ? "register" : "login");
            }}
          >
            {mode === "login" ? "Create an account" : "Sign in"}
            <ArrowUpRight size={15} />
          </button>
        </div>
        <div className="auth-form-wrap">
          <div className="auth-title">
            <span className="eyebrow">YOUR SPACE TO MAKE THINGS HAPPEN</span>
            <h2>
              {mode === "login"
                ? "Welcome back."
                : mode === "register"
                  ? "Make yourself at home."
                  : mode === "recovery"
                    ? "Find your way back."
                    : "Confirm it’s you."}
            </h2>
            <p>
              {mode === "login"
                ? "Sign in and pick up where you left off."
                : mode === "register"
                  ? "Start with you. Bring your team along."
                  : mode === "recovery"
                    ? "Request a secure link to reset your password."
                    : "Request a new link to verify your email address."}
            </p>
          </div>
          {accepted && (
            <div className="notice success" role="status">
              <Check size={19} />
              <div>
                <strong>
                  {requestingLink
                    ? "Your request has been received."
                    : "Your registration has been received."}
                </strong>
                <p>
                  {requestingLink
                    ? "If this address is eligible, instructions will be prepared for delivery. Check your inbox, including your spam folder."
                    : "If this address can be registered, verification instructions will be prepared for delivery. You can sign in after completing verification."}
                </p>
              </div>
            </div>
          )}
          {!requestingLink && (
            <GoogleSignIn
              disabled={pending}
              redirecting={googlePending}
              onRedirecting={setGooglePending}
              callbackError={googleAuthError}
              clearCallbackError={clearGoogleAuthError}
              invitationPending={invitationPending}
            />
          )}
          <form onSubmit={submit} className="auth-form">
            {mode === "register" && (
              <Field label="Your name">
                <input
                  name="name"
                  autoComplete="name"
                  required
                  maxLength={100}
                  placeholder="How should we call you?"
                />
              </Field>
            )}
            <Field label="Email address">
              <input
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                maxLength={254}
                placeholder="you@company.com"
              />
            </Field>
            {!requestingLink && (
              <Field
                label="Password"
                hint={
                  mode === "register"
                    ? "At least 12 characters. A memorable phrase works well."
                    : undefined
                }
              >
                <input
                  name="password"
                  type="password"
                  autoComplete={
                    mode === "register" ? "new-password" : "current-password"
                  }
                  minLength={mode === "register" ? 12 : 1}
                  maxLength={128}
                  required
                  placeholder={
                    mode === "register"
                      ? "Create a strong password"
                      : "Enter your password"
                  }
                />
              </Field>
            )}
            {mode === "register" && (
              <Field
                label="Workspace name"
                hint="A name for your team, studio, or next big idea."
              >
                <input
                  name="workspaceName"
                  required
                  maxLength={100}
                  placeholder="e.g. North Studio"
                />
              </Field>
            )}
            {error && <ErrorNotice message={error} />}
            <button
              className="button primary auth-submit"
              type="submit"
              disabled={busy}
            >
              {pending ? <LoaderCircle size={18} className="spin" /> : null}
              {mode === "login"
                ? "Sign in to your workspace"
                : mode === "register"
                  ? "Create your workspace"
                  : mode === "recovery"
                    ? "Request password reset"
                    : "Request verification link"}
              {!pending && <ArrowRight size={18} />}
            </button>
          </form>
          <div className="auth-assistance">
            {mode === "login" ? (
              <>
                <button onClick={() => changeMode("recovery")} disabled={busy}>
                  Forgot your password?
                </button>
                <button
                  onClick={() => changeMode("verification")}
                  disabled={busy}
                >
                  Resend verification
                </button>
              </>
            ) : requestingLink ? (
              <button onClick={() => changeMode("login")} disabled={busy}>
                Back to sign in
              </button>
            ) : null}
          </div>
          <p className="auth-footnote">
            <ShieldCheck size={16} />
            Your work belongs to you.
          </p>
        </div>
        <footer className="auth-footer">
          <span>Sol Office</span>
          <a className="auth-privacy-link" href="/privacy.html">
            Privacy notice
          </a>
        </footer>
      </section>
    </main>
  );
}

function WorkspaceApp({
  session,
  setSession,
  workspace,
  view,
  navigate,
  historyNotice,
  dismissHistoryNotice,
}: {
  session: SessionResponse;
  setSession: (session: SessionResponse | null) => void;
  workspace?: WorkspaceSummary;
  view: View;
  navigate: (path: string) => void;
  historyNotice: string;
  dismissHistoryNotice: () => void;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [modal, setModal] = useState<
    "workspace" | "invite" | "task" | "jump" | null
  >(null);
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const go = (next: View) => {
    if (workspace) navigate(`/w/${workspace.id}/${next}`);
    setMobileOpen(false);
  };
  const notify = (message: string) => {
    setToast(message);
    setRevision((value) => value + 1);
  };
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 6000);
    return () => clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    setModal(null);
    setWorkspaceOpen(false);
    setError("");
  }, [workspace?.id]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        setModal("jump");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  const logout = async () => {
    if (
      !window.dispatchEvent(
        new Event("sol:before-navigate", { cancelable: true }),
      )
    )
      return;
    try {
      await post<void>("/auth/logout", {});
      setSession(null);
      navigate("/");
    } catch (failure) {
      setError(errorMessage(failure));
    }
  };
  const app = applications.find((item) => item.id === view);
  const title =
    (
      {
        home: "Home",
        members: "Team",
        settings: "Settings",
        apps: "All apps",
      } as Record<string, string>
    )[view] ??
    app?.label ??
    "Workspace";
  const canManage = workspace?.role === "owner" || workspace?.role === "admin";
  return (
    <div className="workspace-app">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      {mobileOpen && (
        <button
          className="sidebar-scrim"
          onClick={() => setMobileOpen(false)}
          aria-label="Close navigation"
        />
      )}
      <aside className={`sidebar ${mobileOpen ? "open" : ""}`}>
        <div className="sidebar-brand">
          <a
            href={workspace ? `/w/${workspace.id}/home` : "/"}
            onClick={(event) => {
              event.preventDefault();
              go("home");
            }}
            className="brand"
          >
            <SolMark />
            <span>
              sol<span className="brand-office">office</span>
            </span>
          </a>
          <button
            className="icon-button mobile-only"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
          >
            <PanelLeftClose size={20} />
          </button>
        </div>
        <div className="workspace-picker">
          <button
            className="workspace-current"
            onClick={() => setWorkspaceOpen(!workspaceOpen)}
            aria-expanded={workspaceOpen}
          >
            <span className="workspace-avatar">
              {initials(workspace?.name ?? "Workspace")}
            </span>
            <span>
              <strong>{workspace?.name ?? "Your workspace"}</strong>
              <small>{workspace?.role ?? "Create a workspace"}</small>
            </span>
            <ChevronDown size={16} />
          </button>
          {workspaceOpen && (
            <div className="workspace-menu">
              {session.workspaces.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    navigate(`/w/${item.id}/home`);
                    setWorkspaceOpen(false);
                  }}
                >
                  <span>{item.name}</span>
                  {item.id === workspace?.id && <Check size={16} />}
                </button>
              ))}
              <button
                className="workspace-create"
                onClick={() => {
                  setWorkspaceOpen(false);
                  setModal("workspace");
                }}
              >
                <Plus size={16} />
                Create workspace
              </button>
            </div>
          )}
        </div>
        <button className="jump-button" onClick={() => setModal("jump")}>
          <Search size={17} />
          <span>Jump to…</span>
          <kbd>⌘ K</kbd>
        </button>
        <nav className="primary-nav" aria-label="Workspace navigation">
          <NavItem
            icon={Home}
            label="Home"
            active={view === "home"}
            onClick={() => go("home")}
          />
          <NavItem
            icon={FolderOpen}
            label="Files"
            active={view === "files"}
            onClick={() => go("files")}
          />
          <NavItem
            icon={CheckSquare2}
            label="Tasks"
            active={view === "tasks"}
            onClick={() => go("tasks")}
          />
          <NavItem
            icon={Bell}
            label="Inbox"
            active={view === "notifications"}
            onClick={() => go("notifications")}
          />
          <NavItem
            icon={FolderKanban}
            label="Projects"
            active={view === "projects"}
            onClick={() => go("projects")}
          />
          <div className="nav-section-label">Create & collaborate</div>
          {applications
            .filter((item) =>
              [
                "documents",
                "spreadsheets",
                "slides",
                "chat",
                "calendar",
              ].includes(item.id),
            )
            .map((item) => (
              <NavItem
                key={item.id}
                icon={item.icon}
                label={item.label}
                active={view === item.id}
                onClick={() => go(item.id)}
              />
            ))}
          <NavItem
            icon={Grid2X2}
            label="All apps"
            active={view === "apps"}
            onClick={() => go("apps")}
          />
          <div className="nav-section-label">Your workspace</div>
          <NavItem
            icon={Users}
            label="Team"
            active={view === "members"}
            onClick={() => go("members")}
          />
          <NavItem
            icon={Settings2}
            label="Settings"
            active={view === "settings"}
            onClick={() => go("settings")}
          />
        </nav>
        <div className="sidebar-bottom">
          <button className="ai-entry" onClick={() => go("ai")}>
            <span>
              <Sparkles size={18} />
              <strong>A little help from Sol AI</strong>
            </span>
            <p>More room for your ideas.</p>
            <ArrowUpRight size={16} className="ai-entry-arrow" />
          </button>
          <div className="account-row">
            <span className="avatar">{initials(session.user.name)}</span>
            <button onClick={() => go("settings")}>
              <strong>{session.user.name}</strong>
              <small>My account</small>
            </button>
            <button
              className="icon-button"
              onClick={() => void logout()}
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut size={17} />
            </button>
          </div>
        </div>
      </aside>
      <div className="workspace-main">
        <header className="topbar">
          <div className="topbar-location">
            <button
              className="icon-button mobile-only"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
            >
              <Menu size={21} />
            </button>
            <span className="topbar-workspace">{workspace?.name}</span>
            <ChevronRight size={14} />
            <span>{title}</span>
          </div>
          <div className="topbar-actions">
            {workspace && (
              <NotificationWidget
                key={`${session.user.id}:${workspace.id}`}
                workspace={workspace}
                navigate={navigate}
              />
            )}
            <span className="workspace-label">
              <span />A shared space
            </span>
            <button
              className="icon-button"
              onClick={() => setModal("jump")}
              aria-label="Browse apps"
            >
              <LayoutGrid size={19} />
            </button>
            <button
              className="avatar small-avatar"
              onClick={() => go("settings")}
              aria-label="Account settings"
            >
              {initials(session.user.name)}
            </button>
          </div>
        </header>
        <main id="main-content" className="main-content" tabIndex={-1}>
          {historyNotice && (
            <div className="verification-banner" role="status">
              <Info size={16} />
              <span>{historyNotice}</span>
              <button onClick={dismissHistoryNotice}>Dismiss</button>
            </div>
          )}
          {!session.user.emailVerified && (
            <div className="verification-banner">
              <ShieldCheck size={16} />
              <span>Your email address has not been verified.</span>
              <button
                onClick={() =>
                  void post("/auth/verification/request", {
                    email: session.user.email,
                  })
                    .then(() =>
                      notify(
                        "Verification instructions have been requested. Check your email after delivery is configured.",
                      ),
                    )
                    .catch((failure) => setError(errorMessage(failure)))
                }
              >
                Request verification link
              </button>
            </div>
          )}
          {error && <ErrorNotice message={error} />}
          <div key={`${workspace?.id}:${view}`} className="page-content">
            {!workspace ? (
              <EmptyState
                icon={Home}
                title="Make a little space."
                action={
                  <button
                    className="button primary"
                    onClick={() => setModal("workspace")}
                  >
                    <Plus size={16} />
                    Create workspace
                  </button>
                }
              >
                Create a workspace to start organizing your work.
              </EmptyState>
            ) : (
              <>
                {view === "home" && (
                  <HomePage
                    workspace={workspace}
                    user={session.user}
                    navigate={navigate}
                    revision={revision}
                    go={go}
                    onCreateTask={() => setModal("task")}
                    onInvite={canManage ? () => setModal("invite") : undefined}
                  />
                )}
                {view === "notifications" && (
                  <Suspense fallback={<Spinner label="Opening inbox…" />}>
                    <NotificationsPage
                      workspace={workspace}
                      navigate={navigate}
                    />
                  </Suspense>
                )}
                {view === "files" && (
                  <FilesPage workspace={workspace} notify={notify} />
                )}
                {view === "tasks" && (
                  <Suspense fallback={<Spinner label="Opening tasks…" />}>
                    <TasksPage
                      workspace={workspace}
                      user={session.user}
                      revision={revision}
                      notify={notify}
                    />
                  </Suspense>
                )}
                {view === "projects" && (
                  <Suspense fallback={<Spinner label="Opening projects…" />}>
                    <ProjectsPage
                      workspace={workspace}
                      user={session.user}
                      revision={revision}
                      notify={notify}
                    />
                  </Suspense>
                )}
                {view === "documents" && (
                  <Suspense fallback={<Spinner label="Opening documents…" />}>
                    <DocumentsPage
                      workspace={workspace}
                      user={session.user}
                      notify={notify}
                    />
                  </Suspense>
                )}
                {view === "members" && (
                  <MembershipPage
                    workspace={workspace}
                    user={session.user}
                    onChanged={async () =>
                      setSession(await api<SessionResponse>("/auth/me"))
                    }
                    revision={revision}
                    onInvite={canManage ? () => setModal("invite") : undefined}
                  />
                )}
                {view === "chat" && (
                  <Suspense
                    fallback={<Spinner label="Opening conversations…" />}
                  >
                    <ChatPage
                      key={workspace.id}
                      workspace={workspace}
                      user={session.user}
                    />
                  </Suspense>
                )}
                {view === "calendar" && (
                  <Suspense fallback={<Spinner label="Opening calendar…" />}>
                    <CalendarPage
                      key={workspace.id}
                      workspace={workspace}
                      user={session.user}
                    />
                  </Suspense>
                )}
                {view === "settings" && (
                  <SettingsPage
                    session={session}
                    workspace={workspace}
                    onCreateWorkspace={() => setModal("workspace")}
                    onLogout={() => void logout()}
                  />
                )}
                {view === "apps" && <AppsPage go={go} />}
                {app && !app.available && <UpcomingPage app={app} go={go} />}
                {![
                  "home",
                  "files",
                  "tasks",
                  "members",
                  "settings",
                  "apps",
                ].includes(view) &&
                  !app && (
                    <EmptyState
                      icon={Search}
                      title="We couldn’t find this page."
                      action={
                        <button
                          className="button primary"
                          onClick={() => go("home")}
                        >
                          Back to home
                        </button>
                      }
                    >
                      Choose a space from the navigation to keep going.
                    </EmptyState>
                  )}
              </>
            )}
          </div>
          <footer className="workspace-footer">
            <span>
              <SolMark small />
              Made for a little more possibility.
            </span>
            <span>Sol Office</span>
          </footer>
        </main>
      </div>
      {toast && (
        <div className="toast" role="status">
          <span>
            <Check size={17} />
          </span>
          {toast}
          <button
            onClick={() => setToast("")}
            aria-label="Dismiss notification"
          >
            <X size={16} />
          </button>
        </div>
      )}
      {modal === "workspace" && (
        <WorkspaceModal
          onClose={() => setModal(null)}
          onCreated={async (created) => {
            const refreshed = await api<SessionResponse>("/auth/me");
            setSession(refreshed);
            setModal(null);
            navigate(`/w/${created.id}/home`);
            notify("Your new workspace is ready.");
          }}
        />
      )}
      {modal === "invite" && workspace && (
        <InviteModal
          workspace={workspace}
          onClose={() => setModal(null)}
          onCreated={(invitation) => {
            setModal(null);
            setRevision((value) => value + 1);
            go("members");
            notify(
              invitation.deliveryStatus === "sent"
                ? "Invitation sent."
                : "Invitation saved as a draft. No email has been sent.",
            );
          }}
        />
      )}
      {modal === "task" && workspace && (
        <Suspense fallback={<Spinner label="Opening new task…" />}>
          <TaskCreateDialog
            workspace={workspace}
            user={session.user}
            onClose={() => setModal(null)}
            onCreated={(task) => {
              setModal(null);
              notify("Your task is ready.");
              navigate(`/w/${workspace.id}/tasks?task=${task.id}`);
            }}
          />
        </Suspense>
      )}
      {modal === "jump" && (
        <JumpModal
          onClose={() => setModal(null)}
          go={(next) => {
            go(next);
            setModal(null);
          }}
        />
      )}
    </div>
  );
}

function NavItem({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`nav-item ${active ? "active" : ""}`}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
    >
      <Icon size={19} strokeWidth={1.65} />
      <span>{label}</span>
      {active && <span className="active-dot" />}
    </button>
  );
}

function ResourceIcon({ resource }: { resource: Resource }) {
  const Icon =
    resource.kind === "folder"
      ? Folder
      : resource.mimeType?.includes("spreadsheet")
        ? FileSpreadsheet
        : resource.mimeType?.includes("presentation")
          ? Presentation
          : FileText;
  return (
    <span
      className={`resource-icon ${resource.kind === "folder" ? "ochre" : "blue"}`}
    >
      <Icon size={21} strokeWidth={1.6} />
    </span>
  );
}

function FilesPage({
  workspace,
  notify,
}: {
  workspace: WorkspaceSummary;
  notify: (message: string) => void;
}) {
  const [resources, setResources] = useState<Resource[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [path, setPath] = useState<Resource[]>([]);
  const [trashed, setTrashed] = useState(false);
  const [query, setQuery] = useState("");
  const [layout, setLayout] = useState<"list" | "grid">("list");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState<"folder" | Resource | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Resource | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [upload, setUpload] = useState<{
    name: string;
    progress: number;
    count: number;
    total: number;
  } | null>(null);
  const [dragging, setDragging] = useState(false);
  const uploadController = useRef<AbortController | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const parentId = path.at(-1)?.id ?? null;
  const load = useCallback(
    async (next?: string) => {
      setLoading(true);
      setError("");
      const params = new URLSearchParams({
        limit: "50",
        trashed: String(trashed),
      });
      if (parentId && !trashed) params.set("parentId", parentId);
      if (next) params.set("cursor", next);
      try {
        const page = await api<Page<Resource>>(
          `/workspaces/${workspace.id}/resources?${params}`,
        );
        setResources((previous) =>
          next ? [...previous, ...page.items] : page.items,
        );
        setCursor(page.nextCursor);
      } catch (failure) {
        setError(errorMessage(failure));
      } finally {
        setLoading(false);
      }
    },
    [workspace.id, parentId, trashed],
  );
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => () => uploadController.current?.abort(), []);
  const uploadFiles = async (selected: FileList | File[]) => {
    if (upload) return;
    const files = Array.from(selected);
    if (!files.length) return;
    if (files.some((file) => file.size > 50 * 1024 * 1024)) {
      setError("Each file can be up to 50 MB. Please choose a smaller file.");
      return;
    }
    setError("");
    const controller = new AbortController();
    uploadController.current = controller;
    let completed = 0;
    try {
      for (const file of files) {
        setUpload({
          name: file.name,
          progress: 0,
          count: completed + 1,
          total: files.length,
        });
        await uploadFile(
          workspace.id,
          file,
          parentId,
          (progress) =>
            setUpload({
              name: file.name,
              progress,
              count: completed + 1,
              total: files.length,
            }),
          controller.signal,
        );
        completed += 1;
      }
      notify(
        completed === 1
          ? "Your file has been uploaded."
          : `${completed} files uploaded.`,
      );
    } catch (failure) {
      setError(
        `${errorMessage(failure)}${completed ? ` ${completed} file${completed > 1 ? "s" : ""} already uploaded.` : ""}`,
      );
    } finally {
      setUpload(null);
      uploadController.current = null;
      if (fileInput.current) fileInput.current.value = "";
      void load();
    }
  };
  const download = async (resource: Resource) => {
    setBusyId(resource.id);
    setError("");
    try {
      await downloadFile(resource);
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setBusyId(null);
    }
  };
  const restore = async (resource: Resource) => {
    setBusyId(resource.id);
    setError("");
    try {
      await post(`/resources/${resource.id}/restore`, {});
      notify("Restored to your workspace.");
      await load();
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setBusyId(null);
    }
  };
  const filtered = resources.filter((item) =>
    item.name.toLocaleLowerCase().includes(query.toLocaleLowerCase()),
  );
  const openResource = (resource: Resource) => {
    if (resource.kind === "folder") {
      setPath([...path, resource]);
      setQuery("");
    } else void download(resource);
  };
  return (
    <>
      <PageHeading
        eyebrow="A PLACE FOR EVERYTHING"
        title="Your files, together."
        description="Shared with the people in this workspace."
      >
        <button
          className="button secondary"
          onClick={() => setDialog("folder")}
          disabled={trashed || Boolean(upload)}
        >
          <Plus size={16} />
          New folder
        </button>
        <button
          className="button primary"
          onClick={() => fileInput.current?.click()}
          disabled={trashed || Boolean(upload)}
        >
          <Upload size={16} />
          Upload files
        </button>
      </PageHeading>
      <input
        ref={fileInput}
        className="visually-hidden"
        type="file"
        multiple
        aria-label="Choose files to upload"
        onChange={(event) => {
          if (event.target.files) void uploadFiles(event.target.files);
        }}
      />
      <div className="collection-toolbar">
        <div className="tabs" role="tablist" aria-label="File location">
          <button
            role="tab"
            aria-selected={!trashed}
            className={!trashed ? "selected" : ""}
            onClick={() => {
              setTrashed(false);
              setQuery("");
            }}
          >
            <FolderOpen size={16} />
            All files
          </button>
          <button
            role="tab"
            aria-selected={trashed}
            className={trashed ? "selected" : ""}
            onClick={() => {
              setTrashed(true);
              setQuery("");
            }}
          >
            <Trash2 size={16} />
            Trash
          </button>
        </div>
        <div className="collection-tools">
          <label className="filter-input">
            <Search size={16} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Filter these files…"
              aria-label="Filter these files"
            />
          </label>
          <div className="layout-switch">
            <button
              className={layout === "list" ? "selected" : ""}
              onClick={() => setLayout("list")}
              aria-label="List view"
              aria-pressed={layout === "list"}
            >
              <ListTodo size={17} />
            </button>
            <button
              className={layout === "grid" ? "selected" : ""}
              onClick={() => setLayout("grid")}
              aria-label="Grid view"
              aria-pressed={layout === "grid"}
            >
              <Grid2X2 size={17} />
            </button>
          </div>
        </div>
      </div>
      {!trashed && (
        <div className="file-breadcrumbs">
          <button onClick={() => setPath([])}>
            <Home size={14} />
            Workspace files
          </button>
          {path.map((folder, index) => (
            <span key={folder.id}>
              <ChevronRight size={13} />
              <button onClick={() => setPath(path.slice(0, index + 1))}>
                {folder.name}
              </button>
            </span>
          ))}
        </div>
      )}
      {error && <ErrorNotice message={error} retry={() => void load()} />}
      {upload && (
        <div className="upload-progress" role="status">
          <div>
            <Upload size={18} />
            <span>
              <strong>
                {upload.progress === 100 ? "Saving" : "Uploading"} {upload.name}
              </strong>
              <small>
                {upload.count} of {upload.total} · {upload.progress}%
              </small>
            </span>
            <button
              className="icon-button"
              onClick={() => uploadController.current?.abort()}
              aria-label="Cancel upload"
            >
              <X size={18} />
            </button>
          </div>
          <progress value={upload.progress} max={100} />
        </div>
      )}
      <section
        className={`file-collection panel ${dragging ? "is-dragging" : ""}`}
        onDragOver={(event) => {
          if (!trashed && !upload) {
            event.preventDefault();
            setDragging(true);
          }
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node))
            setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (!trashed) void uploadFiles(event.dataTransfer.files);
        }}
      >
        {dragging && (
          <div className="drop-overlay">
            <Upload size={32} />
            <strong>Drop your files here</strong>
          </div>
        )}
        {loading && resources.length === 0 ? (
          <div className="panel-loading">
            <Spinner label="Finding your files…" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={trashed ? Trash2 : query ? Search : FolderOpen}
            title={
              query
                ? "No matching files."
                : trashed
                  ? "Nothing in the trash."
                  : path.length
                    ? "This folder has room to grow."
                    : "Your work starts here."
            }
            action={
              !query && !trashed ? (
                <button
                  className="button primary"
                  onClick={() => fileInput.current?.click()}
                >
                  <Upload size={16} />
                  Upload your first file
                </button>
              ) : undefined
            }
          >
            {query ? (
              "Try a different name, or load more files if available."
            ) : trashed ? (
              "Files you move to the trash will appear here, ready to restore."
            ) : (
              <>
                Drop files here or choose them from your device.
                <br />
                Up to 50 MB per file.
              </>
            )}
          </EmptyState>
        ) : layout === "list" ? (
          <div className="table-scroll">
            <table className="data-table files-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Last updated</th>
                  <th>Size</th>
                  <th>
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((resource) => (
                  <tr key={resource.id}>
                    <td>
                      <button
                        className="file-name-button"
                        onClick={() => !trashed && openResource(resource)}
                        disabled={trashed || busyId === resource.id}
                      >
                        <ResourceIcon resource={resource} />
                        <strong>{resource.name}</strong>
                      </button>
                    </td>
                    <td className="muted">{formatDate(resource.updatedAt)}</td>
                    <td className="muted">
                      {resource.kind === "folder"
                        ? "—"
                        : formatBytes(resource.sizeBytes)}
                    </td>
                    <td>
                      <div className="row-actions">
                        {trashed ? (
                          <button
                            className="button compact secondary"
                            onClick={() => void restore(resource)}
                            disabled={busyId === resource.id}
                          >
                            <RotateCcw size={14} />
                            Restore
                          </button>
                        ) : (
                          <>
                            {resource.kind === "file" && (
                              <button
                                className="icon-button"
                                onClick={() => void download(resource)}
                                disabled={busyId === resource.id}
                                aria-label={`Download ${resource.name}`}
                                title="Download"
                              >
                                <ArrowDownToLine size={16} />
                              </button>
                            )}
                            <button
                              className="icon-button"
                              onClick={() => setDialog(resource)}
                              aria-label={`Rename ${resource.name}`}
                              title="Rename"
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              className="icon-button danger-hover"
                              onClick={() => setDeleteTarget(resource)}
                              aria-label={`Move ${resource.name} to trash`}
                              title="Move to trash"
                            >
                              <Trash2 size={15} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="file-grid">
            {filtered.map((resource) => (
              <div className="file-card" key={resource.id}>
                <button
                  className="file-card-main"
                  onClick={() => !trashed && openResource(resource)}
                  disabled={trashed || busyId === resource.id}
                >
                  <div className="file-card-preview">
                    <ResourceIcon resource={resource} />
                  </div>
                  <strong>{resource.name}</strong>
                  <small>
                    {resource.kind === "folder"
                      ? "Folder"
                      : formatBytes(resource.sizeBytes)}{" "}
                    · {formatDate(resource.updatedAt)}
                  </small>
                </button>
                <div className="row-actions">
                  {trashed ? (
                    <button
                      className="text-link"
                      onClick={() => void restore(resource)}
                    >
                      <RotateCcw size={14} />
                      Restore
                    </button>
                  ) : (
                    <>
                      <button
                        className="icon-button"
                        onClick={() => setDialog(resource)}
                        aria-label={`Rename ${resource.name}`}
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        className="icon-button danger-hover"
                        onClick={() => setDeleteTarget(resource)}
                        aria-label={`Move ${resource.name} to trash`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {cursor && (
          <div className="load-more">
            <button
              className="button secondary"
              onClick={() => void load(cursor)}
              disabled={loading}
            >
              {loading ? <Spinner /> : "Load more files"}
            </button>
          </div>
        )}
      </section>
      <p className="collection-footnote">
        <ShieldCheck size={14} />
        Files are shared within {workspace.name}. Downloads keep your original
        file format.
      </p>
      {dialog && (
        <ResourceModal
          resource={dialog === "folder" ? null : dialog}
          parentId={parentId}
          workspaceId={workspace.id}
          onClose={() => setDialog(null)}
          onSaved={() => {
            notify(dialog === "folder" ? "Folder created." : "Name updated.");
            setDialog(null);
            void load();
          }}
        />
      )}
      {deleteTarget && (
        <ConfirmModal
          title="Move to trash?"
          description={`“${deleteTarget.name}” will move to the workspace trash. You can restore it from there.`}
          confirmLabel="Move to trash"
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await api(`/resources/${deleteTarget.id}`, {
              method: "DELETE",
              body: "{}",
            });
            setDeleteTarget(null);
            notify("Moved to trash.");
            await load();
          }}
        />
      )}
    </>
  );
}

function SettingsPage({
  session,
  workspace,
  onCreateWorkspace,
  onLogout,
}: {
  session: SessionResponse;
  workspace: WorkspaceSummary;
  onCreateWorkspace: () => void;
  onLogout: () => void;
}) {
  return (
    <>
      <PageHeading
        eyebrow="MAKE YOURSELF AT HOME"
        title="Your workspace."
        description="Your account, your people, your space to work."
      />
      <section className="panel settings-panel">
        <div className="panel-heading">
          <h2>My account</h2>
          <ShieldCheck size={18} />
        </div>
        <div className="profile-summary">
          <span className="avatar profile-avatar">
            {initials(session.user.name)}
          </span>
          <div>
            <h3>{session.user.name}</h3>
            <p>{session.user.email}</p>
            <span
              className={`verification-label ${session.user.emailVerified ? "verified" : ""}`}
            >
              {session.user.emailVerified ? (
                <Check size={13} />
              ) : (
                <Clock3 size={13} />
              )}
              {session.user.emailVerified
                ? "Email verified"
                : "Email verification pending"}
            </span>
          </div>
        </div>
        <dl className="settings-details">
          <div>
            <dt>Time zone</dt>
            <dd>{session.user.timeZone}</dd>
          </div>
          <div>
            <dt>Account language</dt>
            <dd>{session.user.locale === "th" ? "ไทย / Thai" : "English"}</dd>
          </div>
          <div>
            <dt>Member since</dt>
            <dd>{formatDate(session.user.createdAt)}</dd>
          </div>
        </dl>
      </section>
      <section className="panel settings-panel">
        <div className="panel-heading">
          <h2>Workspace details</h2>
          <span className={`role-badge ${workspace.role}`}>
            {workspace.role}
          </span>
        </div>
        <dl className="settings-details">
          <div>
            <dt>Name</dt>
            <dd>{workspace.name}</dd>
          </div>
          <div>
            <dt>Created</dt>
            <dd>{formatDate(workspace.createdAt)}</dd>
          </div>
          <div>
            <dt>File visibility</dt>
            <dd>Shared with this workspace</dd>
          </div>
        </dl>
        <div className="settings-action">
          <div>
            <h3>A new idea needs a new space?</h3>
            <p>Keep another team or project in its own workspace.</p>
          </div>
          <button className="button secondary" onClick={onCreateWorkspace}>
            <Plus size={16} />
            Create workspace
          </button>
        </div>
      </section>
      <SessionsPanel />
      <section className="signout-section">
        <div>
          <h3>All done for now?</h3>
          <p>Your saved work will be here when you return.</p>
        </div>
        <button className="button secondary" onClick={onLogout}>
          <LogOut size={16} />
          Sign out
        </button>
      </section>
    </>
  );
}

function AppsPage({ go }: { go: (view: View) => void }) {
  return (
    <>
      <PageHeading
        eyebrow="YOUR WORK, FROM EVERY ANGLE"
        title="Room for every idea."
        description="One home for creating, organizing and working together."
      />
      <div className="apps-grid">
        {applications.map(({ icon: Icon, ...app }) => (
          <button key={app.id} className="app-card" onClick={() => go(app.id)}>
            <div>
              <span className={`app-icon ${app.color}`}>
                <Icon size={23} strokeWidth={1.5} />
              </span>
              <span className={`availability ${app.available ? "ready" : ""}`}>
                {app.available ? "Available" : "In progress"}
              </span>
            </div>
            <h2>
              {app.label}
              <ArrowUpRight size={17} />
            </h2>
            <p>{app.description}</p>
          </button>
        ))}
      </div>
    </>
  );
}

function UpcomingPage({
  app,
  go,
}: {
  app: Application;
  go: (view: View) => void;
}) {
  const Icon = app.icon;
  return (
    <div className="upcoming-page">
      <button className="text-link" onClick={() => go("apps")}>
        <ArrowLeft size={16} />
        All apps
      </button>
      <div className="upcoming-content">
        <span className={`app-icon ${app.color}`}>
          <Icon size={36} strokeWidth={1.25} />
        </span>
        <span className="eyebrow">A LITTLE MORE POSSIBILITY, ON THE WAY</span>
        <h1>We’re getting {app.label} ready.</h1>
        <p>
          {app.description} This part of Sol Office is still being built and
          isn’t available to use yet.
        </p>
        <span className="availability">In progress</span>
        <div className="upcoming-actions">
          <button className="button primary" onClick={() => go("files")}>
            <FolderOpen size={17} />
            Go to your files
          </button>
          <button className="button secondary" onClick={() => go("tasks")}>
            <ListTodo size={17} />
            Make a plan
          </button>
        </div>
        <small>
          You can already organize shared files and manage your team’s tasks.
        </small>
      </div>
    </div>
  );
}

function WorkspaceModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (workspace: WorkspaceSummary) => Promise<void>;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = new FormData(event.currentTarget).get("name");
    setPending(true);
    setError("");
    try {
      const { workspace } = await post<{ workspace: WorkspaceSummary }>(
        "/workspaces",
        { name },
      );
      await onCreated(workspace);
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setPending(false);
    }
  };
  return (
    <Modal
      title="A fresh space."
      subtitle="Give your team or project a home of its own."
      onClose={pending ? () => {} : onClose}
    >
      <form onSubmit={submit}>
        <Field label="Workspace name">
          <input
            name="name"
            required
            maxLength={100}
            placeholder="e.g. North Studio"
          />
        </Field>
        {error && <ErrorNotice message={error} />}
        <div className="modal-actions">
          <button
            className="button secondary"
            type="button"
            disabled={pending}
            onClick={onClose}
          >
            Cancel
          </button>
          <button className="button primary" disabled={pending}>
            {pending && <LoaderCircle size={16} className="spin" />}Create
            workspace
          </button>
        </div>
      </form>
    </Modal>
  );
}

function InviteModal({
  workspace,
  onClose,
  onCreated,
}: {
  workspace: WorkspaceSummary;
  onClose: () => void;
  onCreated: (invitation: Invitation) => void;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setPending(true);
    setError("");
    try {
      const { invitation } = await post<{ invitation: Invitation }>(
        `/workspaces/${workspace.id}/invites`,
        { email: data.get("email"), role: data.get("role") },
      );
      onCreated(invitation);
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setPending(false);
    }
  };
  return (
    <Modal
      title="Good work is shared."
      subtitle={`Prepare an invitation to ${workspace.name}.`}
      onClose={pending ? () => {} : onClose}
    >
      <form onSubmit={submit}>
        <Field label="Email address">
          <input
            type="email"
            name="email"
            autoComplete="email"
            required
            maxLength={254}
            placeholder="colleague@company.com"
          />
        </Field>
        <Field label="Workspace role">
          <select name="role" defaultValue="member">
            <option value="member">Member — create and collaborate</option>
            {workspace.role === "owner" && (
              <option value="admin">Admin — manage the workspace</option>
            )}
          </select>
        </Field>
        <div className="notice">
          <Mail size={18} />
          <p>
            Save the invitation, then create a one-time join link in Members.
            Email delivery is not connected.
          </p>
        </div>
        {error && <ErrorNotice message={error} />}
        <div className="modal-actions">
          <button
            className="button secondary"
            type="button"
            disabled={pending}
            onClick={onClose}
          >
            Cancel
          </button>
          <button className="button primary" disabled={pending}>
            {pending && <LoaderCircle size={16} className="spin" />}Save
            invitation draft
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ResourceModal({
  workspaceId,
  resource,
  parentId,
  onClose,
  onSaved,
}: {
  workspaceId: string;
  resource: Resource | null;
  parentId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = new FormData(event.currentTarget).get("name");
    setPending(true);
    setError("");
    try {
      if (resource)
        await patch(`/resources/${resource.id}`, {
          name,
          expectedVersion: resource.version,
        });
      else
        await post(`/workspaces/${workspaceId}/resources`, {
          kind: "folder",
          name,
          parentId,
        });
      onSaved();
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setPending(false);
    }
  };
  return (
    <Modal
      title={resource ? "Give it a new name." : "A place for related things."}
      subtitle={
        resource
          ? "Keep the name clear and easy to find."
          : "Create a folder in this workspace."
      }
      onClose={pending ? () => {} : onClose}
    >
      <form onSubmit={submit}>
        <Field label={resource ? "Name" : "Folder name"}>
          <input
            name="name"
            defaultValue={resource?.name ?? ""}
            required
            maxLength={100}
            placeholder="e.g. Project ideas"
          />
        </Field>
        {error && <ErrorNotice message={error} />}
        <div className="modal-actions">
          <button
            className="button secondary"
            type="button"
            disabled={pending}
            onClick={onClose}
          >
            Cancel
          </button>
          <button className="button primary" disabled={pending}>
            {pending && <LoaderCircle size={16} className="spin" />}
            {resource ? "Save name" : "Create folder"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ConfirmModal({
  title,
  description,
  confirmLabel,
  onClose,
  onConfirm,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  return (
    <Modal title={title} onClose={pending ? () => {} : onClose}>
      <p className="confirm-description">{description}</p>
      {error && <ErrorNotice message={error} />}
      <div className="modal-actions">
        <button
          className="button secondary"
          disabled={pending}
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          className="button danger"
          disabled={pending}
          onClick={async () => {
            setPending(true);
            setError("");
            try {
              await onConfirm();
            } catch (failure) {
              setError(errorMessage(failure));
            } finally {
              setPending(false);
            }
          }}
        >
          {pending && <LoaderCircle size={16} className="spin" />}
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

function JumpModal({
  onClose,
  go,
}: {
  onClose: () => void;
  go: (view: View) => void;
}) {
  const [query, setQuery] = useState("");
  const items = [
    { id: "home", label: "Home", icon: Home, available: true },
    ...applications,
    { id: "members", label: "Team", icon: Users, available: true },
    { id: "settings", label: "Settings", icon: Settings2, available: true },
  ].filter((item) =>
    item.label.toLocaleLowerCase().includes(query.toLocaleLowerCase()),
  );
  return (
    <Modal title="Where would you like to go?" onClose={onClose}>
      <label className="jump-search">
        <Search size={19} />
        <input
          placeholder="Find an app or space…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Find an app or space"
        />
      </label>
      <div className="jump-results">
        {items.map(({ id, label, icon: Icon, available }) => (
          <button key={id} onClick={() => go(id)}>
            <Icon size={19} />
            <strong>{label}</strong>
            {!available && <small>In progress</small>}
            <ArrowRight size={15} />
          </button>
        ))}
        {!items.length && (
          <p className="no-results">No apps match that name.</p>
        )}
      </div>
    </Modal>
  );
}
